import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { SamConfigService } from '@scalefast/config-angular';
import { CustomerService, MaintenanceService } from '@scalefast/ecommerce-core';
import { Subscription } from 'rxjs';
import { BasePage, PageInfo } from './core/interfaces';
import { AppDefaultLayoutComponent } from './layout/default-layout/default-layout.component';
import { PageLayout } from './layout/layout.enum';
import { AnalyticsStoreService } from './shared/services/analytics-store.service';
import { BazaarvoiceService } from './shared/services/bazaarvoice.service';
import { LayoutService } from './shared/services/layout.service';
import { WindowRef } from './shared/services/window-ref.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [AppDefaultLayoutComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  isMaintenanceMode: boolean = false;
  pepStorageKey: string;
  pageLayout: PageInfo<BasePage> | undefined;
  pageLayoutTypes = PageLayout;
  private layoutSubscription: Subscription | undefined;
  private resolveLayoutSubscription: Subscription | undefined;

  constructor(
    public router: Router,
    private customerService: CustomerService,
    private maintenanceService: MaintenanceService,
    private analyticsStoreService: AnalyticsStoreService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject('WindowRef') private windowRef: WindowRef,
    private layoutService: LayoutService,
    private samConfigService: SamConfigService,
    private bazaarvoiceService: BazaarvoiceService,
  ) {
    this.isMaintenanceMode = this.maintenanceService.getCurrentMaintenanceMode();
    if (this.isMaintenanceMode) {
      this.pageLayout = { id: PageLayout.Maintenance, data: null };
    }

    this.pepStorageKey = this.samConfigService.get().apiContext.v4.pepStorageKey;

    if (isPlatformBrowser(this.platformId)) {
      this.tryLoginM2M();
      const window = this.windowRef.nativeWindow;
      window.addEventListener('storage', this.handleStorageEvent, false);
      this.getV4SessionStorage();
      setTimeout(() => this.analyticsStoreService.getEvents());
    }
  }

  ngOnInit() {
    this.layoutSubscription = this.layoutService.pageLayout$.subscribe((layout) => {
      if (!this.simpleComparisonObject(layout, this.pageLayout)) {
        this.pageLayout = layout;
      }
    });

    this.resolveLayoutSubscription = this.layoutService.resolveLayoutForCurrentRoute().subscribe((layout) => {
      if (layout) {
        this.layoutService.setPageLayout(layout);
      }
    });

    this.bazaarvoiceService.injectScript();
  }

  private tryLoginM2M() {
    const credentials = this.customerService.getHybridStoreCredentials();
    if (credentials) {
      this.customerService.loginM2M(credentials);
    } else {
      this.customerService.removeCredentialsM2M();
    }
  }

  handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === 'v4_getSessionStorage') {
      localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
      localStorage.removeItem('sessionStorage');
    } else if (event.key === 'sessionStorage') {
      const sessionStorage = JSON.parse(event.newValue || '{}');
      for (const key in sessionStorage) {
        if (key === this.pepStorageKey) {
          window.sessionStorage.setItem(key, sessionStorage[key]);
          this.customerService.loginM2M(JSON.parse(sessionStorage[key])?.access_token);
        }
      }
    }
  };

  getV4SessionStorage(): void {
    if (!sessionStorage.length) {
      const current = new Date().toLocaleTimeString();
      localStorage.setItem('v4_getSessionStorage', 'request session storage' + current);
    }
  }

  error(event: unknown) {
    console.log('Builder error', event);
  }

  ngOnDestroy() {
    this.layoutSubscription?.unsubscribe();
    this.resolveLayoutSubscription?.unsubscribe();
  }

  private simpleComparisonObject(object1: any, object2: any): boolean {
    return JSON.stringify(object1) === JSON.stringify(object2);
  }
}
